import { useState, useEffect, useRef } from 'react';
import { FileCheck2, Upload, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '@/lib/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ALLOWED_EXTENSIONS = ['.pdf', '.jpg', '.jpeg', '.png', '.doc', '.docx'];
const EXT_TO_MIME = {
  '.pdf': 'application/pdf',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.png': 'image/png',
  '.doc': 'application/msword',
  '.docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

const fmtDate = (v) => formatDate(v) || '—';

/**
 * FunderFinalContractCard
 *
 * Renders only on funded applications where the current funder owns the
 * accepted offer. Lets the funder upload the executed contract (PDF/JPEG/DOCX).
 * Reuses the standard presigned-URL + confirm-upload flow with the fixed
 * category `funder_final_contract` — the backend rejects any other category
 * for the funder role.
 *
 * Also lists any previously uploaded final contracts so the funder can see
 * their upload history.
 */
const FunderFinalContractCard = ({ applicationId, getAuthHeaders }) => {
  const [contracts, setContracts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const fetchContracts = async () => {
    try {
      const r = await fetch(`${API}/applications/${applicationId}/documents`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        const all = d.documents || [];
        setContracts(all.filter(x => x.document_category === 'funder_final_contract'));
      }
    } catch { /* soft-fail */ }
  };

  useEffect(() => { fetchContracts(); }, [applicationId]);

  const openDoc = async (documentId) => {
    try {
      const r = await fetch(`${API}/documents/${documentId}/download-url`, {
        credentials: 'include', headers: getAuthHeaders(),
      });
      if (r.ok) {
        const d = await r.json();
        window.open(d.download_url, '_blank', 'noopener');
      } else {
        toast.error('Failed to open contract');
      }
    } catch { toast.error('Failed to open contract'); }
  };

  const handlePick = () => fileRef.current?.click();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = '';
    if (!file) return;
    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      toast.error(`File type not allowed. Allowed: ${ALLOWED_EXTENSIONS.join(', ')}`);
      return;
    }
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25MB).'); return; }
    const contentType = file.type && file.type !== 'application/octet-stream' ? file.type : EXT_TO_MIME[ext];

    setUploading(true);
    try {
      const urlRes = await fetch(`${API}/applications/${applicationId}/documents/upload-url`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          filename: file.name,
          content_type: contentType,
          file_size: file.size,
          category: 'funder_final_contract',
        }),
      });
      if (!urlRes.ok) {
        const err = await urlRes.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to start upload');
      }
      const { document_id, upload_url } = await urlRes.json();

      const putRes = await fetch(upload_url, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
      if (!putRes.ok) throw new Error('Failed to upload to storage');

      const confirmRes = await fetch(`${API}/documents/${document_id}/confirm-upload`, {
        method: 'POST', credentials: 'include', headers: getAuthHeaders(),
      });
      if (!confirmRes.ok) throw new Error('Failed to confirm upload');

      toast.success('Final contract uploaded');
      fetchContracts();
    } catch (e) {
      console.error('Final contract upload failed:', e);
      toast.error(e.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="mcl-card" style={{ marginBottom: '1.25rem' }} data-testid="funder-final-contract-card">
      <div className="mcl-card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 className="mcl-card-header-title" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <FileCheck2 size={16} /> Final Funding Contract
        </h3>
        <input
          ref={fileRef} type="file" style={{ display: 'none' }}
          accept={ALLOWED_EXTENSIONS.join(',')}
          onChange={handleFile} disabled={uploading}
          data-testid="funder-contract-file-input"
        />
        <button
          type="button"
          className="mcl-btn mcl-btn-sm mcl-btn-primary"
          onClick={handlePick}
          disabled={uploading}
          data-testid="funder-contract-upload-btn"
          style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <Upload size={13} /> {uploading ? 'Uploading…' : 'Upload Contract'}
        </button>
      </div>
      <div className="mcl-card-body">
        <div style={{ fontSize: '0.82rem', color: 'var(--mcl-text-muted)', marginBottom: '0.6rem' }}>
          Upload the executed funding contract for your records. Accepted formats: PDF, JPG, PNG, DOC, DOCX (max 25MB).
        </div>
        <div style={{
          fontSize: '0.75rem', color: 'var(--mcl-text-muted)',
          padding: '0.5rem 0.75rem', background: '#eff6ff', border: '1px dashed #bfdbfe',
          borderRadius: 6, marginBottom: '0.75rem',
        }} data-testid="docusign-future-hint">
          <strong>Coming soon:</strong> connect your DocuSign account to sign, execute, and auto-file
          the contract inside MyCaseLoan.com — with an automated check that the platform fee is
          reflected in the final terms.
        </div>
        {contracts.length === 0 ? (
          <div style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem', padding: '0.5rem 0' }} data-testid="funder-contract-empty">
            No final contracts uploaded yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {contracts.map((c) => (
              <div
                key={c.document_id}
                data-testid={`funder-contract-row-${c.document_id}`}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.55rem 0.75rem', background: '#f8fafc', borderRadius: 6, fontSize: '0.85rem' }}
              >
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{c.original_filename}</div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>Uploaded {fmtDate(c.created_at)}</div>
                </div>
                <button
                  type="button"
                  className="mcl-btn mcl-btn-sm mcl-btn-outline"
                  onClick={() => openDoc(c.document_id)}
                  data-testid={`funder-contract-open-${c.document_id}`}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                >
                  <ExternalLink size={12} /> View
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FunderFinalContractCard;
